import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import { playerAPI } from '../../services/api';
import { publicAssetUrl } from '../../utils/publicAssetUrl';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: #0f172a;
`;

const CloseBtn = styled.button`
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  color: #475569;
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.45;
`;

const TeamCard = styled.button`
  display: flex;
  gap: 0.65rem;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid ${(p) => (p.$selected ? '#043663' : '#e2e8f0')};
  border-radius: 12px;
  background: ${(p) => (p.$selected ? 'rgba(4, 54, 99, 0.06)' : '#fff')};
  cursor: pointer;
`;

const Avatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #e2e8f0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  resize: vertical;
  margin-bottom: 0.75rem;
  box-sizing: border-box;
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 10px;
  background: #043663;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0.5rem 0;
`;

/**
 * @param {object} props
 * @param {string} props.targetPlayerId
 * @param {string} [props.targetPlayerName]
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSuccess]
 */
export default function RequestJoinToPlayerModal({
  targetPlayerId,
  targetPlayerName,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [message, setMessage] = useState('');

  const { data, isLoading } = useQuery(
    ['playerJoinableTeams', targetPlayerId],
    () => playerAPI.getPlayerJoinableTeams(targetPlayerId),
    { enabled: !!targetPlayerId, staleTime: 30 * 1000 }
  );

  const teams = Array.isArray(data?.data) ? data.data : [];

  useEffect(() => {
    if (teams.length === 1 && !selectedTeamId) {
      setSelectedTeamId(teams[0].teamId);
    }
  }, [teams, selectedTeamId]);

  const joinMutation = useMutation(
    (payload) => playerAPI.createTeamJoinRequest(payload.teamId, { message: payload.message }),
    {
      onSuccess: (res) => {
        if (res?.success === false) {
          toast.error(res?.message || t('playerTeamsPage.toastRequestFailed'));
          return;
        }
        toast.success(t('playerTeamsPage.toastJoinSent'));
        onSuccess?.();
        onClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('playerTeamsPage.toastRequestFailed'));
      },
    }
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Modal role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('playerTeamsPage.requestJoinToPlayerTitle')}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label={t('playerTeamsPage.close')}>
            <FaTimes aria-hidden />
          </CloseBtn>
        </Header>
        <Hint>
          {t('playerTeamsPage.requestJoinToPlayerHint', {
            name: targetPlayerName || t('playerTeamsPage.playerFallback'),
          })}
        </Hint>

        {isLoading ? (
          <Empty>{t('playerTeamsPage.loadingShort')}</Empty>
        ) : teams.length === 0 ? (
          <Empty>{t('playerTeamsPage.requestJoinNoTeams')}</Empty>
        ) : (
          teams.map((team) => {
            const img = team.teamLogoUrl ? publicAssetUrl(team.teamLogoUrl) : '';
            const name = team.teamName || t('playerTeamsPage.teamFallback');
            return (
              <TeamCard
                key={team.teamId}
                type="button"
                $selected={selectedTeamId === team.teamId}
                onClick={() => setSelectedTeamId(team.teamId)}
              >
                <Avatar>
                  {img ? <img src={img} alt="" /> : name.slice(0, 2).toUpperCase()}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    {team.canton || '—'}
                    {team.divisionName ? ` · ${team.divisionName}` : ''}
                    {team.teamSkillLevel != null
                      ? ` · ${t('playerTeamsPage.skillPrefix')} ${team.teamSkillLevel}`
                      : ''}
                  </div>
                </div>
              </TeamCard>
            );
          })
        )}

        {teams.length > 0 ? (
          <>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('playerTeamsPage.requestPlaceholder')}
              maxLength={1000}
            />
            <SendBtn
              type="button"
              disabled={!selectedTeamId || joinMutation.isLoading}
              onClick={() =>
                joinMutation.mutate({
                  teamId: selectedTeamId,
                  message: message.trim() || undefined,
                })
              }
            >
              <FaPaperPlane aria-hidden />
              {joinMutation.isLoading
                ? t('playerTeamsPage.sending')
                : t('playerTeamsPage.sendJoinRequest')}
            </SendBtn>
          </>
        ) : null}
      </Modal>
    </Overlay>
  );
}
